import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { tierForViewport } from "../utils/timeSeriesTiles/resolution";
import { type AssembledSeries, assembleSeries } from "../utils/timeSeriesTiles/selector";
import { emptyStore, tileReducer } from "../utils/timeSeriesTiles/store";
import { tilesForViewport } from "../utils/timeSeriesTiles/tiles";
import {
  type FetchTile,
  type ResolutionLadder,
  type Tier,
  type TileRef,
  tileKey,
  type Viewport,
} from "../utils/timeSeriesTiles/types";
import { useTileLoader } from "./useTileLoader";

const HYSTERESIS = 1.3;
const COST_GUARD = 250_000;
const DEFAULT_TILE_COST_SERIES_COUNT = 3;

function computeOverscan(span: number, tier: Tier, tileCostSeriesCount: number): number {
  const bucketsPerTile = tier.tileSpanMs / tier.bucketMs;
  const maxTiles = Math.max(1, Math.floor(COST_GUARD / bucketsPerTile / tileCostSeriesCount));
  return Math.min(Math.max(1, Math.ceil((0.5 * span) / tier.tileSpanMs)), maxTiles);
}

export interface UseTileWindowArgs {
  fetchTile: FetchTile;
  ladder: ResolutionLadder;
  nativeMs: number;
  /** Caller-owned camera. The window re-tiles as this changes. */
  viewport: Viewport;
  widthPx: number;
  maxFutureMs?: number;
  targetPointsPerPx?: number;
  concurrency?: number;
  /**
   * Approximate backend row multiplier for one bucket, used by the cost guard.
   * Defaults to the voltage envelope's three fetched series (avg/min/max) to
   * preserve the current behaviour; a single-series source can pass 1.
   */
  tileCostSeriesCount?: number;
  /** Changing this aborts in-flight fetches and clears the store (e.g. a new meter). */
  resetKey?: string;
  refreshKey?: string;
  refreshEnabled?: boolean;
  /** Missing tiles are automatic only when the camera advances on its own. */
  automaticLoads?: boolean;
}

export interface UseTileWindowResult {
  tier: Tier;
  series: AssembledSeries;
  loading: boolean;
  refreshing: boolean;
  refreshError: Error | undefined;
  atDataStart: boolean;
}

export interface UseTiledTimeSeriesArgs extends Omit<UseTileWindowArgs, "viewport"> {
  initialViewport: Viewport;
}

export interface UseTiledTimeSeriesResult extends UseTileWindowResult {
  viewport: Viewport;
  onViewportChange(v: Viewport): void;
  jumpTo(w: Viewport): void;
}

export function useTileWindow(args: UseTileWindowArgs): UseTileWindowResult {
  const {
    fetchTile,
    ladder,
    nativeMs,
    viewport,
    widthPx,
    maxFutureMs,
    targetPointsPerPx = 1,
    concurrency = 3,
    tileCostSeriesCount = DEFAULT_TILE_COST_SERIES_COUNT,
    resetKey,
    refreshKey,
    refreshEnabled,
    automaticLoads,
  } = args;
  const lodOpts = useMemo(() => ({ targetPointsPerPx, nativeMs }), [targetPointsPerPx, nativeMs]);

  const [tier, setTier] = useState<Tier>(() =>
    tierForViewport(viewport.endMs - viewport.startMs, Math.max(1, widthPx), ladder, lodOpts)
  );
  const [storedTiles, dispatch] = useReducer(tileReducer, emptyStore);
  const [storeIdentity, setStoreIdentity] = useState(resetKey);
  const store = storeIdentity === resetKey ? storedTiles : emptyStore;
  const prevSeriesRef = useRef<AssembledSeries | null>(null);
  const prevSeriesViewportRef = useRef<Viewport | null>(null);
  if (storeIdentity !== resetKey) {
    setStoreIdentity(resetKey);
    prevSeriesRef.current = null;
    prevSeriesViewportRef.current = null;
    dispatch({ type: "reset" });
  }

  // Tier selection with hysteresis on the span since the last switch.
  const lastSwitchSpan = useRef(viewport.endMs - viewport.startMs);
  const lastSwitchLod = useRef(lodOpts);
  useEffect(() => {
    const span = viewport.endMs - viewport.startMs;
    const ideal = tierForViewport(span, Math.max(1, widthPx), ladder, lodOpts);
    if (ideal.id === tier.id) {
      lastSwitchLod.current = lodOpts;
      return;
    }
    // The deadband damps thrash while zooming, so it is measured on the span. A LOD
    // change is not a zoom — it moves the ideal for the SAME span, leaving the ratio
    // at exactly 1 — so it has to bypass the deadband, or a source that discovers
    // its cadence asynchronously stays pinned to the tier its fallback picked until
    // the user happens to zoom.
    //
    // Ask whether the LOD change itself moved the ideal, not merely whether the
    // inputs changed: with a zoom already suppressed, the ideal differs from the
    // current tier for a reason the deadband deliberately rejected, and an inert
    // cadence update would otherwise release that pending span-driven switch.
    const idealUnderLastLod = tierForViewport(span, Math.max(1, widthPx), ladder, lastSwitchLod.current);
    const lodMovedIdeal = idealUnderLastLod.id !== ideal.id;
    const ratio = span / lastSwitchSpan.current;
    if (lodMovedIdeal || ratio > HYSTERESIS || ratio < 1 / HYSTERESIS) {
      lastSwitchSpan.current = span;
      lastSwitchLod.current = lodOpts;
      setTier(ideal);
    }
  }, [viewport, widthPx, ladder, tier.id, lodOpts]);

  const span = viewport.endMs - viewport.startMs;
  const overscanTiles = useMemo(
    () => computeOverscan(span, tier, tileCostSeriesCount),
    [span, tier, tileCostSeriesCount]
  );

  const refs = useMemo(() => tilesForViewport(viewport, tier, overscanTiles), [viewport, tier, overscanTiles]);
  const refsKey = useMemo(() => refs.map((r) => tileKey(r.tierId, r.index)).join(","), [refs]);
  const refsRef = useRef<TileRef[]>(refs);
  refsRef.current = refs;
  const viewportRef = useRef<Viewport>(viewport);
  viewportRef.current = viewport;
  const storeRef = useRef(store);
  storeRef.current = store;

  useTileLoader({
    refs,
    refsKey,
    store,
    tier,
    viewport,
    fetchTile,
    dispatch,
    concurrency,
    resetKey,
    refreshKey,
    refreshEnabled,
    automaticLoads,
    ...(maxFutureMs != null ? { maxFutureMs } : {}),
  });

  // Memoized on in-view tile identities, readiness, and accepted result revisions:
  // a tile landing OUTSIDE the current refs (e.g. a slow stale-tier fetch) leaves
  // seriesKey unchanged, so the series array keeps a stable reference and the
  // renderer never re-diffs during a pan (R15). Reads the latest store via ref.
  const seriesKey = useMemo(
    () =>
      refsRef.current
        .map((r) => {
          const id = tileKey(r.tierId, r.index);
          const entry = store.get(id);
          return `${id}:${entry?.status ?? "∅"}:${entry?.revision ?? 0}:${!!entry?.error}`;
        })
        .join("|"),
    [refsKey, store]
  );
  const series = useMemo(() => {
    const result = assembleSeries({
      viewport: viewportRef.current,
      targetTier: tier,
      ladder,
      store: storeRef.current,
      overscanTiles,
      prev: prevSeriesRef.current,
      prevViewport: prevSeriesViewportRef.current,
      maxFutureMs,
    });
    prevSeriesRef.current = result;
    prevSeriesViewportRef.current = viewportRef.current;
    return result;
    // viewport.start/endMs: a committed pan that lands on the same tile-id set
    // still moved the camera, so re-assemble to keep coverage/isStale/fallback
    // honest. Viewport state only changes on settle (never per live frame), so
    // this can't recompute mid-gesture and preserves the R15 pan stability.
  }, [seriesKey, tier, ladder, overscanTiles, viewport.startMs, viewport.endMs, maxFutureMs, resetKey]);

  const loading = useMemo(
    () => refsRef.current.some((r) => storeRef.current.get(tileKey(r.tierId, r.index))?.status === "loading"),
    [seriesKey]
  );
  const atDataStart = useMemo(() => {
    const first = refsRef.current[0];
    return first ? storeRef.current.get(tileKey(first.tierId, first.index))?.status === "empty" : false;
  }, [seriesKey]);

  const relevantEntries = refs.map((r) => store.get(tileKey(r.tierId, r.index)));
  const refreshing = relevantEntries.some((entry) => entry?.refreshing);
  const refreshError = relevantEntries.find((entry) => entry?.error)?.error;
  return { tier, series, loading, refreshing, refreshError, atDataStart };
}

export function useTiledTimeSeries(args: UseTiledTimeSeriesArgs): UseTiledTimeSeriesResult {
  const { initialViewport, ...windowArgs } = args;
  const [viewport, setViewport] = useState<Viewport>(initialViewport);
  const window = useTileWindow({ ...windowArgs, viewport });

  const onViewportChange = useCallback((v: Viewport) => setViewport(v), []);
  const jumpTo = useCallback((w: Viewport) => setViewport(w), []);

  return { viewport, onViewportChange, jumpTo, ...window };
}
